/**
 * List-specific API functions
 * 
 * Lists use different endpoints and payload structures than Itineraries.
 * Lists use POST /api/v1/post; the response is normally a full saved post (plus optional write metadata).
 */

import axios from 'axios';
import Post from '@/models/post';
import { getPostById } from './itinerary';
import { debug, log, warn } from '@/utils/logger';

interface ApiError extends Error {
  status?: number;
  response?: unknown;
}

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

export const CREATE_OR_UPDATE_LIST_POST = `${API_URL}/api/v1/post`;

/** Keys merged into POST /api/v1/post response that are not part of Post.toJSON(). */
const LIST_POST_SAVE_EXTRA_KEYS = new Set([
  'insertedId',
  'acknowledged',
  'modifiedCount',
  'matchedCount',
  'upsertedId',
  'newPoints',
]);

function responseLooksLikeSavedPost(payload: unknown): payload is Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const d = payload as Record<string, unknown>;
  return typeof d._id === 'string' && d._id.length > 0;
}

/**
 * Create or update a List post
 * 
 * @param data - List post payload
 * @returns Full Post object from save response (includes server `updated_at`) or from getPostById fallback
 */
export async function createOrUpdateListPost(
  data: Record<string, unknown>
): Promise<Post> {
  try {
    const requestPostId = typeof data?._id === 'string' ? data._id : undefined;

    const payloadPostLocations = Array.isArray(data.postLocations) ? data.postLocations as Array<Record<string, unknown>> : [];
    log('[createOrUpdateListPost] REQUEST — wire payload', {
      postId: requestPostId ?? null,
      postLocationsCount: payloadPostLocations.length,
      postLocationNames: payloadPostLocations.map(pl => (pl.location as Record<string, unknown> | undefined)?.name ?? pl.body ?? '(unnamed)'),
      postLocationIds: payloadPostLocations.map(pl => pl._id ?? '(no _id)'),
      postLocationLocationIds: payloadPostLocations.map(pl => pl.location_id ?? '(no location_id)'),
      postLocationGooglePlaceIds: payloadPostLocations.map(pl => pl.google_place_id ?? (pl.location as Record<string, unknown> | undefined)?.google_place_id ?? '(none)'),
    });

    debug('[createOrUpdateListPost] POST /api/v1/post', {
      postId: requestPostId ?? null,
      postLocationsCount: payloadPostLocations.length,
    });

    const response = await axios.post(CREATE_OR_UPDATE_LIST_POST, data);
    const rawResponseData = response.data as Record<string, unknown>;

    const rawPostLocations = Array.isArray(rawResponseData.postLocations)
      ? rawResponseData.postLocations as Array<Record<string, unknown>>
      : Array.isArray(rawResponseData.post_locations)
        ? rawResponseData.post_locations as Array<Record<string, unknown>>
        : [];
    log('[createOrUpdateListPost] RESPONSE — raw', {
      postId: rawResponseData._id ?? '(no _id)',
      rawPostLocationsCount: rawPostLocations.length,
      rawPostLocationNames: rawPostLocations.map(pl => (pl.location as Record<string, unknown> | undefined)?.name ?? pl.body ?? '(unnamed)'),
      rawPostLocationIds: rawPostLocations.map(pl => pl._id ?? '(no _id)'),
      responseLooksLikeSavedPost: responseLooksLikeSavedPost(rawResponseData),
    });

    // Backend now returns saved post JSON + legacy write fields (insertedId, acknowledged, …).
    if (responseLooksLikeSavedPost(rawResponseData)) {
      try {
        const cleaned: Record<string, unknown> = { ...rawResponseData };
        for (const k of LIST_POST_SAVE_EXTRA_KEYS) {
          delete cleaned[k];
        }
        const post = new Post(cleaned);
        if (post._id) {
          log('[createOrUpdateListPost] PARSED — post from response', {
            postId: post._id,
            post_locations_count: post.post_locations?.length ?? 0,
            postLocations_count: post.postLocations?.length ?? 0,
            postLocationNames: post.postLocations?.map((pl) => {
              const loc = pl.location as { name?: string } | undefined;
              return loc?.name ?? pl.body ?? '(unnamed)';
            }),
          });
          debug('[createOrUpdateListPost] save response parsed as full post', {
            postId: post._id,
            post_locations_count: post.post_locations?.length ?? 0,
          });
          return post;
        }
      } catch (err) {
        warn('[createOrUpdateListPost] Failed to parse full post from save response; falling back to getPostById', err);
      }
    }

    // Legacy shape or parse failure: Mongo-style body only
    const legacy = rawResponseData as {
      insertedId?: string;
      upsertedId?: string;
    };
    const postId =
      legacy.insertedId ||
      legacy.upsertedId ||
      requestPostId;

    if (!postId) {
      throw new Error('Failed to get post ID from List save response');
    }

    debug('[createOrUpdateListPost] Fetching post after save (legacy or unparsable response)', {
      postId,
    });
    return getPostById(postId);
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };
    const status = axiosError?.response?.status;
    const message =
      axiosError?.response?.data?.message ||
      axiosError?.message ||
      'Unknown error';

    // Handle specific error cases based on backend API documentation
    if (status === 403) {
      const errorMessage =
        "Unauthorized: You don't have permission to edit this list";
      const customError: ApiError = new Error(errorMessage);
      customError.status = 403;
      customError.response = axiosError?.response;
      throw customError;
    } else if (status === 404) {
      const errorMessage = 'List not found';
      const customError: ApiError = new Error(errorMessage);
      customError.status = 404;
      customError.response = axiosError?.response;
      throw customError;
    } else if (status === 400) {
      const errorMessage = `Invalid request: ${message}`;
      const customError: ApiError = new Error(errorMessage);
      customError.status = 400;
      customError.response = axiosError?.response;
      throw customError;
    }

    // Re-throw original error for other cases
    throw error;
  }
}

/**
 * Get a List post by ID
 * 
 * @param postId - Post ID
 * @returns List Post object
 */
export async function getListPost(postId: string): Promise<Post> {
  // Lists use the same getPostById endpoint as other posts
  return getPostById(postId);
}

